import React from "react";

const IQ_MIN = 55;
const IQ_MAX = 145;
const MEAN = 100;
const SD = 15;

const SVG_W = 320;
const SVG_H = 190;
const CHART_LEFT = 44;
const CHART_RIGHT = 304;
const CHART_TOP = 30;
const CHART_BOTTOM = 148;

const iqToX = (iq) =>
  CHART_LEFT + ((iq - IQ_MIN) / (IQ_MAX - IQ_MIN)) * (CHART_RIGHT - CHART_LEFT);

const pdfNorm = (iq) => {
  const z = (iq - MEAN) / SD;
  return Math.exp(-0.5 * z * z);
};

const pdfToY = (norm) => CHART_BOTTOM - norm * (CHART_BOTTOM - CHART_TOP);

function buildCurvePoints() {
  const pts = [];
  for (let iq = IQ_MIN; iq <= IQ_MAX; iq += 0.5) {
    pts.push(`${iqToX(iq).toFixed(2)},${pdfToY(pdfNorm(iq)).toFixed(2)}`);
  }
  return pts;
}

const curvePoints = buildCurvePoints();
const fillPath = `M${iqToX(IQ_MIN)},${CHART_BOTTOM} L${curvePoints.join(" L")} L${iqToX(IQ_MAX)},${CHART_BOTTOM}Z`;
const linePath = `M${curvePoints.join(" L")}`;

const SD_LABELS = [
  { iq: 55, label: "0.11" },
  { iq: 70, label: "21" },
  { iq: 85, label: "142" },
  { iq: 100, label: "342" },
  { iq: 115, label: "342" },
  { iq: 130, label: "142" },
  { iq: 145, label: "21" },
];

const AXIS_TICKS = [55, 70, 85, 100, 115, 130, 145];

const IQBellCurveCard = ({ iqScore = 100, onUnlock }) => {
  const scoreX = iqToX(iqScore);
  const scoreNorm = pdfNorm(iqScore);
  const scoreY = pdfToY(scoreNorm);

  return (
    <div className="rounded-[20px] overflow-hidden bg-[#111D3A]">
      {/* Chart SVG */}
      <svg
        viewBox={`0 0 ${SVG_W} ${SVG_H}`}
        width="100%"
        className="block"
        aria-label="IQ Score Bell Curve"
      >
        {/* Background */}
        <rect width={SVG_W} height={SVG_H} fill="#111D3A" />

        {/* Defs */}
        <defs>
          <linearGradient id="iqCurveFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3B82C4" stopOpacity="0.75" />
            <stop offset="100%" stopColor="#1B3A6B" stopOpacity="0.3" />
          </linearGradient>
          <linearGradient id="iqCurveStroke" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#60A5FA" />
            <stop offset="50%" stopColor="#93C5FD" />
            <stop offset="100%" stopColor="#60A5FA" />
          </linearGradient>
          {/* Clip to chart area */}
          <clipPath id="chartClip">
            <rect
              x={CHART_LEFT}
              y={CHART_TOP}
              width={CHART_RIGHT - CHART_LEFT}
              height={CHART_BOTTOM - CHART_TOP}
            />
          </clipPath>
        </defs>

        {/* Title */}
        <text
          x={SVG_W / 2}
          y={18}
          textAnchor="middle"
          fill="white"
          fontSize="11"
          fontWeight="700"
          fontFamily="Sora, sans-serif"
          letterSpacing="0.02em"
        >
          IQ Score Bell Curve
        </text>

        {/* Y-axis label */}
        <text
          x={10}
          y={(CHART_TOP + CHART_BOTTOM) / 2}
          textAnchor="middle"
          fill="#94A3B8"
          fontSize="7"
          fontFamily="Manrope, sans-serif"
          transform={`rotate(-90, 10, ${(CHART_TOP + CHART_BOTTOM) / 2})`}
          letterSpacing="0.04em"
        >
          Frequency in population
        </text>

        {/* SD segment shading stripes (alternating opacity) */}
        {[55, 70, 85, 100, 115, 130].map((iq, i) => (
          <rect
            key={iq}
            x={iqToX(iq)}
            y={CHART_TOP}
            width={iqToX(iq + 15) - iqToX(iq)}
            height={CHART_BOTTOM - CHART_TOP}
            fill={
              i % 2 === 0 ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.04)"
            }
          />
        ))}

        {/* Filled area under curve */}
        <path
          d={fillPath}
          fill="url(#iqCurveFill)"
          clipPath="url(#chartClip)"
        />

        {/* Curve line */}
        <path
          d={linePath}
          fill="none"
          stroke="url(#iqCurveStroke)"
          strokeWidth="2"
          clipPath="url(#chartClip)"
        />

        {/* User score: vertical highlight */}
        <line
          x1={scoreX}
          y1={CHART_TOP}
          x2={scoreX}
          y2={CHART_BOTTOM}
          stroke="#FF8C6B"
          strokeWidth="1.5"
          strokeDasharray="3 2"
        />
        {/* Score dot on curve */}
        <circle
          cx={scoreX}
          cy={scoreY}
          r="4"
          fill="#FF8C6B"
          stroke="#fff"
          strokeWidth="1.5"
        />
        {/* Score label above dot */}
        <rect
          x={scoreX - 16}
          y={scoreY - 22}
          width="32"
          height="15"
          rx="4"
          fill="#FF8C6B"
        />
        <text
          x={scoreX}
          y={scoreY - 11}
          textAnchor="middle"
          fill="white"
          fontSize="8"
          fontWeight="700"
          fontFamily="Sora, sans-serif"
        >
          {iqScore}
        </text>

        {/* SD frequency labels */}
        {SD_LABELS.map(({ iq, label }) => {
          const x = iqToX(iq);
          const norm = pdfNorm(iq);
          const y = pdfToY(norm) - 5;
          return (
            <text
              key={iq}
              x={x}
              y={y < CHART_TOP + 8 ? CHART_TOP + 10 : y}
              textAnchor="middle"
              fill="rgba(255,255,255,0.55)"
              fontSize="6"
              fontFamily="Manrope, sans-serif"
            >
              {label}
            </text>
          );
        })}

        {/* X-axis baseline */}
        <line
          x1={CHART_LEFT}
          y1={CHART_BOTTOM}
          x2={CHART_RIGHT}
          y2={CHART_BOTTOM}
          stroke="rgba(255,255,255,0.2)"
          strokeWidth="1"
        />

        {/* X-axis tick labels */}
        {AXIS_TICKS.map((iq) => (
          <text
            key={iq}
            x={iqToX(iq)}
            y={CHART_BOTTOM + 11}
            textAnchor="middle"
            fill="#94A3B8"
            fontSize="7"
            fontFamily="Manrope, sans-serif"
          >
            {iq}
          </text>
        ))}

        {/* X-axis label */}
        <text
          x={CHART_LEFT}
          y={CHART_BOTTOM + 22}
          fill="#64748B"
          fontSize="6.5"
          fontFamily="Manrope, sans-serif"
        >
          IQ SCORE:
        </text>

        {/* "Average IQ score is 100" annotation */}
        <text
          x={iqToX(100)}
          y={SVG_H - 4}
          textAnchor="middle"
          fill="#FF8C6B"
          fontSize="7"
          fontWeight="600"
          fontFamily="Sora, sans-serif"
        >
          Average IQ score is 100
        </text>
      </svg>
    </div>
  );
};

export default IQBellCurveCard;
